import Delete from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { Unstable_LicenseInfoProvider } from '@mui/x-license';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { cs } from 'date-fns/locale/cs';
import { enUS } from 'date-fns/locale/en-US';
import 'mchat/dist/styles/base.css';
import { type AppProps } from 'next/app';
import { SessionProvider, useSession } from 'next-auth/react';
import { closeSnackbar, SnackbarProvider } from 'notistack';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import 'reactflow/dist/style.css';
import { shallow } from 'zustand/shallow';

import '../assets/scss/global.scss';
import '../assets/scss/mchat-overrides.scss';
import '../components/bot/nodes/node-styles.css';
import '../components/dynamic-flow-editor/components/editor-search-bar.css';
import '../components/dynamic-flow-editor/components/json-editor.css';
import '../components/dynamic-flow-editor/components/popup.css';
import '../components/dynamic-flow-editor/components/validation-banner.css';
import '../components/dynamic-flow-editor/editor-global.css';
// Base DFE editor styles must load BEFORE the mportal-theme overrides so
// the override file (imported last) wins same-specificity cascade collisions.
// eslint-disable-next-line sort-imports, import/order
import '../components/dynamic-flow-editor/editor.css';
// eslint-disable-next-line sort-imports, import/order
import '../components/dynamic-flow-editor/editor-overrides.css';
import { useTelmaPortalStore } from '../store/telma-portal-store';
import themes from '../themes';

import Layout from '@/components/layout';
import { SessionGate } from '@/components/session-gate';
import { useMuiLicense } from '@/hooks/use-mui-license';
import { useKeyboardListener } from '@/utils/keyboard-shortcuts';
import { getUserType } from '@/utils/session/user-preferences';
import 'i18n';

const queryClient = new QueryClient();

function AppContent({
    Component,
    pageProps,
}: {
    Component: AppProps['Component'];
    pageProps: any;
}) {
    const { data: session } = useSession();
    const isHGC = useMemo(() => getUserType(session), [session]);
    const { info: muiLicenseInfo } = useMuiLicense();

    useKeyboardListener();
    const customization = useTelmaPortalStore(
        (state) => ({
            opened: state.opened,
            setMenu: state.setMenu,
        }),
        shallow,
    );
    const { i18n } = useTranslation();

    const clientTheme = useMemo(
        () => themes(customization, isHGC ? 'hgc' : 'telma'),
        [customization, isHGC],
    );

    return (
        <Unstable_LicenseInfoProvider info={muiLicenseInfo}>
            <ThemeProvider theme={clientTheme}>
                <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={i18n.language == 'cs' ? cs : enUS}
                >
                    <SnackbarProvider
                        autoHideDuration={6000}
                        preventDuplicate={true}
                        maxSnack={4}
                        action={(snackbarId) => (
                            <IconButton
                                onClick={() => closeSnackbar(snackbarId)}
                            >
                                <Delete />
                            </IconButton>
                        )}
                    >
                        <SessionGate>
                            <Layout>
                                <Component {...pageProps} />
                            </Layout>
                        </SessionGate>
                    </SnackbarProvider>
                </LocalizationProvider>
            </ThemeProvider>
        </Unstable_LicenseInfoProvider>
    );
}

export default function App({
    Component,
    pageProps: { session, ...pageProps },
}: AppProps) {
    return (
        <QueryClientProvider client={queryClient}>
            <SessionProvider session={session} refetchInterval={5 * 60}>
                <AppContent Component={Component} pageProps={pageProps} />
            </SessionProvider>
        </QueryClientProvider>
    );
}
